import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { DashboardTile } from "components/Dashboard/DashboardTile";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { formatShipmentRangeDate } from "../formatShipmentRangeDate";
import { TileContentGreyBackground } from "./TileContentGreyBackground";
import { useHasWasteShipmentData } from "./useHasWasteShipmentData";
import { WasteCodes } from "./WasteCodes";
import { useWasteFacilityState } from "./WasteFacilityContext";
import { WasteStreamsDataGrid } from "./WasteStreamsDataGrid/WasteStreamsDataGrid";

type WasteStreamsDatagridTypes = "waste-streams" | "waste-codes";

const datagridVariations: Record<
  WasteStreamsDatagridTypes,
  { label: string; selectLabel: string }
> = {
  "waste-streams": {
    label: "Waste Streams",
    selectLabel: "Waste Streams",
  },
  "waste-codes": {
    label: "Waste Codes",
    selectLabel: "Waste Codes",
  },
};

export const WasteStreamsTile = () => {
  return (
    <DashboardTile size={{ xs: 12 }}>
      <WasteStreamsTileContent />
    </DashboardTile>
  );
};

const WasteStreamsTileContent = () => {
  const theme = useTheme();
  const { facilityId } = useParams<{ facilityId: string }>();
  const {
    wasteFacilityState: { optimisticFilters },
  } = useWasteFacilityState();
  const hasShipmentData = useHasWasteShipmentData();

  const [selectedGraphType, setSelectedGraphType] =
    useState<WasteStreamsDatagridTypes>("waste-streams");

  const formattedDateRange = useMemo(() => {
    return formatShipmentRangeDate({
      startDate: optimisticFilters.startDate,
      endDate: optimisticFilters.endDate,
    });
  }, [optimisticFilters.startDate, optimisticFilters.endDate]);

  return (
    <>
      <DashboardTile.Header
        title="Waste Streams"
        infoTooltip={
          <Stack spacing={2}>
            <Typography variant="caption">
              Waste streams are created based on the unique combination of
              Profile Description, Federal Waste Codes, State Waste Codes, Form
              Code, and Source Code.
            </Typography>
          </Stack>
        }
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="caption" color="textSecondary">
            {formattedDateRange}
          </Typography>
          <FormControl sx={{ width: theme.spacing(32.5) }}>
            <InputLabel id="view-by">View by</InputLabel>
            <Select
              sx={{ height: theme.spacing(5) }}
              fullWidth
              labelId="view-by"
              label="View by"
              value={selectedGraphType}
              onChange={(event) => {
                setSelectedGraphType(event.target.value);
              }}
            >
              {Object.entries(datagridVariations).map(
                ([key, { selectLabel }]) => (
                  <MenuItem key={key} value={key}>
                    {selectLabel}
                  </MenuItem>
                )
              )}
            </Select>
          </FormControl>
        </Stack>
      </DashboardTile.Header>
      <DashboardTile.ContentArea minHeight={hasShipmentData ? 550 : undefined}>
        {!hasShipmentData ? (
          <TileContentGreyBackground>
            🧾 No waste stream data to show
          </TileContentGreyBackground>
        ) : (
          <>
            {selectedGraphType === "waste-streams" && (
              <WasteStreamsDataGrid facilityId={facilityId ?? ""} />
            )}
            {selectedGraphType === "waste-codes" && (
              <WasteCodes facilityId={facilityId ?? ""} />
            )}
          </>
        )}
      </DashboardTile.ContentArea>
    </>
  );
};
